import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import MediaPicker from '../components/public/MediaPicker';
import PublicFormShell from '../components/public/PublicFormShell';
import { uploadMedia } from '../lib/publicMedia';

const MAX_MESSAGE = 1000;

const FEEDBACK_TYPES = [
  { value: 'complaint', icon: '⚠️' },
  { value: 'suggestion', icon: '💡' },
  { value: 'praise', icon: '⭐' },
  { value: 'concern', icon: '🤔' },
  { value: 'mood', icon: '😊' },
  { value: 'other', icon: '📝' },
];

// The old list — Management, Coworkers, Workload, Benefits — was written for
// staff using the employee app. This page is only ever reached by scanning a
// printed code, so the person answering is a guest at a table and none of those
// words describe anything they can see.
const CATEGORIES = ['food', 'service', 'cleanliness', 'price', 'waiting', 'other'];

const TRIAGE_TYPES = new Set(['complaint', 'concern']);
const URGENCIES = ['low', 'medium', 'high', 'critical'];

function StepHeading({ n, title, hint }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand-peach font-serif text-base font-bold text-brand-clay ring-1 ring-brand-peachLine">
        {n}
      </span>
      <div>
        <h2 className="font-serif text-lg font-bold leading-snug text-brand-ink sm:text-xl">{title}</h2>
        {hint && <p className="mt-0.5 text-sm text-brand-body">{hint}</p>}
      </div>
    </div>
  );
}

export default function Feedback({ qrLink }) {
  const { t } = useTranslation();

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    feedback_type: '',
    category: '',
    mood_score: null,
    message: '',
    urgency: 'medium',
    contact: '',
    wants_followup: false,
  });

  // Chosen but not yet uploaded — MediaPicker owns the choosing and its own
  // size/count errors; upload happens on submit.
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState('');
  const [submitError, setSubmitError] = useState('');

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const resetForm = () => {
    setSubmitted(false);
    setFiles([]);
    setSubmitError('');
    setForm({
      feedback_type: '', category: '', mood_score: null, message: '',
      urgency: 'medium', contact: '', wants_followup: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!form.feedback_type) {
      setSubmitError(t('feedback.selectType'));
      return;
    }

    setSubmitting(true);
    try {
      let uploadedAttachments = [];
      if (files.length > 0) {
        uploadedAttachments = await uploadMedia(files, setUploadProgress);
      }

      // A guest has no employee id. If they asked to be contacted back, the
      // detail they left rides along in the message — there is no column for
      // it, and inventing one for a free-text phone number is worse.
      let message = form.message?.trim() || null;
      const contact = form.wants_followup ? form.contact.trim() : '';
      if (contact) {
        message = `${message ? message + '\n\n' : ''}${t('feedback.contactNote', { contact })}`;
      }

      // anonymous_feedback is not writable by anon — submissions go through
      // the validating submit_public_feedback RPC.
      const { data: result, error } = await supabase.rpc('submit_public_feedback', {
        p_feedback_type: form.feedback_type,
        p_message: message,
        p_branch_id: qrLink?.branch_id || null,
        p_category: form.category || null,
        p_mood_score: form.mood_score,
        p_urgency: TRIAGE_TYPES.has(form.feedback_type) ? form.urgency : 'medium',
        p_employee_id: null,
        p_wants_followup: form.wants_followup,
        p_attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
        // Which printed QR this came from ('suci', 'arcamanik'). Set on the
        // QR row by HR, so it cannot be forged from the URL.
        p_source: qrLink?.source || null,
      });

      if (error) throw error;
      if (result && result.success === false) throw new Error(result.error || 'submit_failed');
      setSubmitted(true);
      setFiles([]);
      setUploadProgress('');
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setSubmitError(err?.message || t('feedback.submitFailed'));
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  if (submitted) {
    return (
      <PublicFormShell
        title={t('feedback.thankYouTitle')}
        subtitle={t('feedback.headerSubtitle')}
        narrow
      >
        <div className="rounded-3xl bg-white p-8 text-center shadow-[0_8px_30px_-12px_rgba(29,43,34,0.18)] ring-1 ring-brand-line">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-peach text-3xl">
            ✅
          </div>
          <p className="mt-4 text-base leading-relaxed text-brand-body">{t('feedback.thankYouBody')}</p>
          <button
            type="button"
            onClick={resetForm}
            className="mt-6 rounded-full border border-brand-peachLine bg-white px-6 py-2.5 text-sm font-semibold text-brand-clay transition-colors hover:bg-brand-peach"
          >
            {t('feedback.submitAnother')}
          </button>
        </div>
      </PublicFormShell>
    );
  }

  const showMood = form.feedback_type === 'mood';
  const showDetail = form.feedback_type && form.feedback_type !== 'mood';

  return (
    <PublicFormShell
      title={t('feedback.headerTitle')}
      subtitle={t('feedback.headerSubtitle')}
      description={t('feedback.headerDesc')}
      badge={t('feedback.anonymousBadge')}
    >
      <form onSubmit={handleSubmit} className="rounded-3xl bg-white p-5 shadow-[0_8px_30px_-12px_rgba(29,43,34,0.18)] ring-1 ring-brand-line sm:p-7">
        {/* 1 — type */}
        <section>
          <StepHeading n="1" title={`${t('feedback.typeQuestion')} *`} hint={t('feedback.typeHint')} />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {FEEDBACK_TYPES.map((type) => {
              const on = form.feedback_type === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => set({ feedback_type: type.value, urgency: 'medium' })}
                  className={`relative rounded-2xl border-2 p-3 text-center transition-all sm:p-4 ${
                    on
                      ? 'border-brand-clay bg-brand-peach shadow-sm'
                      : 'border-brand-line bg-white hover:border-brand-peachLine hover:bg-brand-peach/40'
                  }`}
                >
                  {on && (
                    <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-clay text-[11px] text-white">
                      ✓
                    </span>
                  )}
                  <span className={`mx-auto flex h-11 w-11 items-center justify-center rounded-full text-xl ${on ? 'bg-white' : 'bg-brand-peach'}`}>
                    {type.icon}
                  </span>
                  <span className={`mt-2 block text-sm font-bold ${on ? 'text-brand-clay' : 'text-brand-ink'}`}>
                    {t(`feedback.types.${type.value}`)}
                  </span>
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-brand-body">
                    {t(`feedback.typeDesc.${type.value}`)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {showMood && (
          <section className="mt-7">
            <StepHeading n="2" title={t('feedback.moodQuestion')} />
            <div className="flex justify-between gap-2">
              {[
                { score: 1, emoji: '😢' }, { score: 2, emoji: '😔' }, { score: 3, emoji: '😐' },
                { score: 4, emoji: '🙂' }, { score: 5, emoji: '😊' },
              ].map(({ score, emoji }) => {
                const on = form.mood_score === score;
                return (
                  <button
                    key={score}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set({ mood_score: score })}
                    className={`flex-1 rounded-2xl border-2 py-2.5 transition-all ${
                      on ? 'border-brand-clay bg-brand-peach' : 'border-brand-line bg-white hover:border-brand-peachLine'
                    }`}
                  >
                    <span className="block text-2xl">{emoji}</span>
                    <span className="mt-1 block text-[11px] text-brand-body">
                      {t(`feedback.moods.${['veryBad', 'bad', 'okay', 'good', 'great'][score - 1]}`)}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* 2 — message */}
        <section className="mt-7">
          <StepHeading n={showMood ? '3' : '2'} title={`${t('feedback.messageLabel')} ${showMood ? '' : '*'}`.trim()} hint={t('feedback.messageHint')} />

          {showDetail && (
            <div className="mb-3 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const on = form.category === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set({ category: on ? '' : c })}
                    className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                      on
                        ? 'border-brand-clay bg-brand-clay text-white'
                        : 'border-brand-line bg-white text-brand-body hover:border-brand-peachLine hover:bg-brand-peach/50'
                    }`}
                  >
                    {t(`feedback.custCategories.${c}`)}
                  </button>
                );
              })}
            </div>
          )}

          <textarea
            value={form.message}
            onChange={(e) => set({ message: e.target.value.slice(0, MAX_MESSAGE) })}
            rows={5}
            maxLength={MAX_MESSAGE}
            placeholder={t('feedback.messagePlaceholder')}
            className="w-full rounded-2xl border border-brand-line bg-white p-4 text-[15px] leading-relaxed text-brand-ink placeholder:text-brand-body/60 focus:border-brand-clay focus:outline-none focus:ring-2 focus:ring-brand-clay/20"
          />
          <div className="mt-1 text-right text-xs text-brand-body/70">
            {form.message.length}/{MAX_MESSAGE}
          </div>

          {/* Kept off the mock deliberately: "the toilet is flooding" and "the
              music is loud" are both complaints, and the inbox has no other way
              to tell them apart. Only shown where it can mean something. */}
          {TRIAGE_TYPES.has(form.feedback_type) && (
            <div className="mt-3">
              <div className="mb-2 text-sm font-medium text-brand-body">{t('feedback.urgencyLabel')}</div>
              <div className="flex gap-2">
                {URGENCIES.map((u) => {
                  const on = form.urgency === u;
                  return (
                    <button
                      key={u}
                      type="button"
                      aria-pressed={on}
                      onClick={() => set({ urgency: u })}
                      className={`flex-1 rounded-xl border py-2 text-[13px] font-semibold transition-colors ${
                        on
                          ? 'border-brand-clay bg-brand-peach text-brand-clay'
                          : 'border-brand-line bg-white text-brand-body hover:border-brand-peachLine'
                      }`}
                    >
                      {t(`feedback.urgency.${u}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* 3 — attachments */}
        <section className="mt-7">
          <StepHeading
            n={showMood ? '4' : '3'}
            title={`${t('feedback.attachmentsLabel')} (${t('feedback.optional')})`}
            hint={t('feedback.attachmentsHint')}
          />
          <MediaPicker files={files} onChange={setFiles} />
          <p className="mt-2 text-xs text-brand-body/70">{t('feedback.attachmentsFormats')}</p>
        </section>

        {/* follow-up */}
        <div className="mt-7 rounded-2xl bg-brand-peach/50 p-4 ring-1 ring-brand-peachLine">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={form.wants_followup}
              onChange={(e) => set({ wants_followup: e.target.checked })}
              className="mt-1 h-4 w-4 flex-none rounded border-brand-peachLine text-brand-clay focus:ring-brand-clay"
            />
            <span>
              <span className="block text-sm font-bold text-brand-ink">{t('feedback.followupLabel')}</span>
              <span className="mt-0.5 block text-sm text-brand-body">{t('feedback.followupHint')}</span>
            </span>
          </label>

          {form.wants_followup && (
            <div className="mt-3">
              <input
                type="text"
                value={form.contact}
                onChange={(e) => set({ contact: e.target.value })}
                placeholder={t('feedback.contactPlaceholder')}
                className="w-full rounded-xl border border-brand-peachLine bg-white p-3 text-sm text-brand-ink placeholder:text-brand-body/60 focus:border-brand-clay focus:outline-none focus:ring-2 focus:ring-brand-clay/20"
              />
              <p className="mt-1.5 text-xs text-brand-body/80">{t('feedback.contactHint')}</p>
            </div>
          )}
        </div>

        {submitError && (
          <p className="mt-5 rounded-xl bg-red-50 p-3 text-center text-sm text-red-700" role="alert">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !form.feedback_type}
          className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-brand-clay py-4 text-base font-bold text-white shadow-sm transition-colors hover:bg-brand-clayDark disabled:cursor-not-allowed disabled:opacity-45"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" />
          </svg>
          {submitting
            ? (uploadProgress ? t('feedback.uploading', { progress: uploadProgress }) : t('feedback.submitting'))
            : t('feedback.submit')}
        </button>

        <p className="mt-3 text-center text-xs leading-relaxed text-brand-body/80">
          {t('feedback.anonymousNote')}
        </p>
      </form>
    </PublicFormShell>
  );
}
